import { motion } from 'framer-motion';
import { Landmark, Scale, BookOpen, Gavel } from 'lucide-react';

// Only Landmark/BookOpen icons used — no gavels/scales per brand.
const CASES = [
  {
    name: 'Zealand v Minister of Justice and Constitutional Development',
    citation: '2008 (4) SA 458 (CC)',
    point: 'Confirms that any deprivation of freedom must be lawful and just — the State bears the onus of justifying detention.',
  },
  {
    name: 'Minister of Safety and Security v Sekhoto',
    citation: '2011 (5) SA 367 (SCA)',
    point: 'Sets out the four jurisdictional requirements for lawful arrest without a warrant under section 40(1)(b) of the CPA.',
  },
  {
    name: 'Minister of Safety and Security v Tyulu',
    citation: '2009 (5) SA 85 (SCA)',
    point: 'Foundational authority on quantum — the primary purpose of an award for unlawful arrest is to compensate the plaintiff.',
  },
  {
    name: 'Woji v Minister of Police',
    citation: '2015 (1) SACR 409 (SCA)',
    point: 'Where the police cause a subsequent, continued detention post-first-appearance, they may also be liable for that period.',
  },
];

export default function LandmarkCases() {
  return (
    <section data-testid="landmark-cases" className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">Chapter 06 — The law behind the claim</div>
          <h2 className="mt-4 font-poppins font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.02]">
            Leading South African <span className="text-[#39A8FF]">authorities.</span>
          </h2>
          <p className="mt-5 font-inter text-white/70 text-base md:text-lg leading-relaxed">
            The principles our courts apply. Each case turns on its own facts — these decisions provide the framework.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {CASES.map((c, i) => (
            <motion.article
              key={c.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              data-testid={`landmark-${i}`}
              className="relative rounded-3xl border border-white/[0.06] bg-[#0d1826]/60 p-7 md:p-8 hover:border-[#0A84FF]/50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <Landmark className="h-6 w-6 text-[#39A8FF]" strokeWidth={1.4} />
                <span className="font-inter text-[10px] font-bold uppercase tracking-[0.28em] text-white/45">Precedent</span>
              </div>
              <h3 className="mt-6 font-poppins font-semibold text-white text-lg md:text-xl leading-snug italic">{c.name}</h3>
              <div className="mt-2 font-inter text-[13px] text-[#FFD400] font-semibold tracking-wide">{c.citation}</div>
              <p className="mt-5 font-inter text-white/65 text-[14.5px] leading-relaxed">{c.point}</p>
            </motion.article>
          ))}
        </div>

        <p className="mt-10 font-inter text-white/45 text-xs md:text-sm max-w-3xl">
          Case names and citations are provided for educational reference only. They do not constitute legal advice and outcomes vary. Each matter is assessed on its own facts and the applicable law.
        </p>
      </div>
    </section>
  );
}
